import React from 'react';
import { Plus, Pencil, Trash2, X } from 'lucide-react';
import { Button } from './ui/button';
import { Label } from './ui/label';

/**
 * Reusable CRUD Table used in Settings page.
 * On mobile: renders items as cards. On sm+: shows table.
 */
export function CrudTable({
  title,
  items,
  columns,
  onAdd,
  onEdit,
  onDelete,
  canAdd = true,
  canEdit = true,
  canDelete = true,
  renderExtra,
  addLabel = 'Add',
}) {
  return (
    <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200">
        <h3 className="text-sm font-semibold text-slate-700">{title}</h3>
        {canAdd && (
          <Button size="sm" onClick={onAdd} className="bg-blue-600 hover:bg-blue-700 text-white text-xs gap-1">
            <Plus size={13} /> {addLabel}
          </Button>
        )}
      </div>

      {/* Desktop table — hidden on mobile */}
      <div className="hidden sm:block overflow-x-auto">
        <table className="w-full erp-table">
          <thead>
            <tr>
              {columns.map(c => <th key={c.key}>{c.label}</th>)}
              <th className="text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 ? (
              <tr>
                <td colSpan={columns.length + 1} className="py-8 text-center text-slate-400 text-sm">
                  No {title.toLowerCase()} yet
                </td>
              </tr>
            ) : items.map((item, idx) => (
              <tr key={item.id || idx}>
                {columns.map(c => (
                  <td key={c.key} className={c.className || ''}>
                    {c.render ? c.render(item) : (item[c.key] || '—')}
                  </td>
                ))}
                <td>
                  <div className="flex items-center justify-end gap-1">
                    {renderExtra && renderExtra(item)}
                    {canEdit && (
                      <button onClick={() => onEdit(item)} className="p-1.5 text-slate-400 hover:text-blue-600 rounded">
                        <Pencil size={13} />
                      </button>
                    )}
                    {canDelete && (
                      <button onClick={() => onDelete(item)} className="p-1.5 text-slate-400 hover:text-red-600 rounded">
                        <Trash2 size={13} />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile cards — shown only on mobile */}
      <div className="sm:hidden">
        {items.length === 0 ? (
          <div className="py-10 text-center text-slate-400 text-sm">
            No {title.toLowerCase()} yet
          </div>
        ) : items.map((item, idx) => (
          <div key={item.id || idx} className="border-b border-slate-100 last:border-0 px-4 py-3">
            <div className="flex items-start justify-between gap-2">
              {/* Card content: show all columns stacked */}
              <div className="flex-1 min-w-0 space-y-1">
                {columns.map((c, ci) => (
                  <div key={c.key} className={ci === 0 ? 'flex items-center gap-2' : 'flex items-baseline gap-2'}>
                    {ci > 0 && (
                      <span className="text-[10px] font-semibold text-slate-400 uppercase tracking-wide w-16 shrink-0">
                        {c.label}
                      </span>
                    )}
                    <span className={`${ci === 0 ? 'text-sm font-semibold text-slate-800 truncate' : 'text-xs text-slate-600 truncate'}`}>
                      {c.render ? c.render(item) : (item[c.key] || '—')}
                    </span>
                  </div>
                ))}
              </div>
              {/* Action buttons */}
              <div className="flex items-center gap-1 shrink-0 mt-0.5">
                {renderExtra && renderExtra(item)}
                {canEdit && (
                  <button
                    onClick={() => onEdit(item)}
                    className="p-2 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-md"
                  >
                    <Pencil size={15} />
                  </button>
                )}
                {canDelete && (
                  <button
                    onClick={() => onDelete(item)}
                    className="p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-md"
                  >
                    <Trash2 size={15} />
                  </button>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * Reusable Modal dialog used in Settings page.
 * Full-height scrollable on mobile.
 */
export function Modal({ title, children, onClose, onSave, saving }) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40"
      onClick={e => e.target === e.currentTarget && onClose()}
    >
      <div className="bg-white w-full sm:max-w-md sm:rounded-lg shadow-xl flex flex-col max-h-[92vh] sm:max-h-[88vh] rounded-t-2xl sm:rounded-2xl">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200 shrink-0">
          <h3 className="text-sm font-semibold text-slate-800">{title}</h3>
          <button onClick={onClose} className="p-1.5 text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded-md">
            <X size={16} />
          </button>
        </div>
        {/* Scrollable body */}
        <div className="px-5 py-4 space-y-4 overflow-y-auto flex-1">{children}</div>
        {/* Footer */}
        <div className="flex justify-end gap-3 px-5 py-4 border-t border-slate-100 shrink-0">
          <Button variant="outline" size="sm" onClick={onClose} className="min-h-[40px] px-5">Cancel</Button>
          <Button
            size="sm"
            onClick={onSave}
            disabled={saving}
            className="bg-blue-600 hover:bg-blue-700 text-white min-h-[40px] px-5"
          >
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </div>
      </div>
    </div>
  );
}

/**
 * Labelled form field wrapper used in Settings modals.
 */
export function FormField({ label, children, hint }) {
  return (
    <div>
      <Label className="text-xs font-semibold text-slate-600 uppercase tracking-wide mb-1 block">
        {label}
      </Label>
      {children}
      {hint && <p className="text-xs text-slate-400 mt-1">{hint}</p>}
    </div>
  );
}
